import {
  Button,
  Checkbox,
  Divider,
  Form,
  InputNumber,
  Select,
} from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import { FC, ReactNode, useEffect, useState } from "react";

const defaultFilterValues: any = {
  Prix_de_vente: [0, 50_000_000],
  Type_de_bien: [],
  m2_habitable: [0, 10_000],
  m2_terrain: [0, 10_000],
  Type_de_contrat: [],
  style_architectural: [],
  Loyer_par_m2: [0, 10_000],
  showStatistics: false,
};

const BiensOffMarketFilters: FC<{
  onApplyFilters: (filters: any) => void;
}> = ({ onApplyFilters }) => {
  const [form] = Form.useForm();
  const [filters, setFilters] = useState({});
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => handleFormValuesChange(defaultFilterValues), []);

  useEffect(() => {
    onApplyFilters(filters);
  }, [filters, onApplyFilters]);

  const handleFormValuesChange = (allValues: any) => {
    let filters: any[] = [];
    Object.keys(allValues).forEach((field) => {
      if (
        JSON.stringify(allValues[field]) ===
        JSON.stringify(defaultFilterValues[field])
      ) {
        return;
      }

      switch (field) {
        case "Prix_de_vente":
          const prixRange = [
            allValues[field][0] ?? 0,
            allValues[field][1] ?? 1000000000
          ];
          filters.push({ Prix_de_vente: { _between: prixRange } });
          break;

        case "Type_de_bien":
          if (allValues[field].length > 0) {
            const typeFilters: any[] = [];
            allValues[field].forEach((v: any) => {
              if (v === "_empty") {
                typeFilters.push({ Type_de_bien: { _empty: true } });
              } else {
                typeFilters.push({ Type_de_bien: { _eq: v } });
              }
            });
            filters.push({ _or: typeFilters });
          }
          break;

        case "m2_habitable":
          const habitableRange = [
            allValues[field][0] ?? 0,
            allValues[field][1] ?? 1000000000
          ];
          filters.push({
            m2_habitable: { _between: habitableRange },
          });
          break;

        case "m2_terrain":
          const terrainRange = [
            allValues[field][0] ?? 0,
            allValues[field][1] ?? 1000000000
          ];
          filters.push({ m2_terrain: { _between: terrainRange } });
          break;

        case "Type_de_contrat":
          if (allValues[field].length > 0) {
            const contratFilters: any[] = [];
            allValues[field].forEach((v: any) => {
              if (v === "_empty") {
                contratFilters.push({ Type_de_contrat: { _empty: true } });
              } else {
                contratFilters.push({ Type_de_contrat: { _eq: v } });
              }
            });
            filters.push({ _or: contratFilters });
          }
          break;

        case "style_architectural":
          if (allValues[field].length > 0) {
            const styleFilters: any[] = [];
            allValues[field].forEach((v: any) => {
              if (v === "_empty") {
                styleFilters.push({ style_architectural: { _empty: true } });
              } else {
                styleFilters.push({ style_architectural: { _eq: v } });
              }
            });
            filters.push({ _or: styleFilters });
          }
          break;


        case "Loyer_par_m2":
          const loyerRange = [
            allValues[field][0] ?? 0,
            allValues[field][1] ?? 1000000000
          ];
          filters.push({ Loyer_par_m2: { _between: loyerRange } });
          break;

        case "showStatistics":
          // This field is handled separately in the status filter logic
          break;

        default:
          break;
      }
    });

    // Handle status filter based on showStatistics checkbox
    const statusFilter = allValues.showStatistics 
      ? { status: { _in: ["actif", "statistique"] } }
      : { status: { _eq: "actif" } };

    setFilters({ 
      _and: [
        { Position_sur_map: { _nnull: true } },
        statusFilter,
        ...filters
      ]
    });
  };

  return (
    <Form
      form={form}
      initialValues={defaultFilterValues}
      style={{ padding: "16px 24px" }}
      layout="vertical"
      size="small"
      onValuesChange={(_, allValues) => handleFormValuesChange(allValues)}
    >
      <div
        style={{
          fontSize: 18,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          padding: "8px 0",
        }}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {isCollapsed ? <RightOutlined /> : <DownOutlined />}
          <span>Filtres biens off-market</span>
        </div>
        {!isCollapsed && (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              form.resetFields();
              handleFormValuesChange(defaultFilterValues);
            }}
          >
            Réinitialiser
          </Button>
        )}
      </div>
      {!isCollapsed && (
        <>
          <Divider style={{ margin: "16px 0" }} />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
             <Form.Item label="Prix de vente - CHF" name={["Prix_de_vente", 0]}>
               <InputNumber step={10000} type="number" />
             </Form.Item>
             <Form.Item label=" " name={["Prix_de_vente", 1]}>
               <InputNumber step={10000} type="number" />
             </Form.Item>
          </div>

          <FilterField label="Type de bien" name="Type_de_bien">
            <Select
              mode="multiple"
              allowClear
              style={{ width: "100%" }}
              options={[
                { label: "(vide)", value: "_empty" },
                { label: "Maison", value: "maison" },
                { label: "Appartement", value: "appartement" },
                { label: "Immeuble", value: "immeuble" },
                { label: "Bureau", value: "bureau" },
                { label: "Arcade", value: "arcade" },
              ]}
            />
          </FilterField>

          <FilterField label="Type de contrat" name="Type_de_contrat">
            <Select
              mode="multiple"
              allowClear
              style={{ width: "100%" }}
              options={[
                { label: "(vide)", value: "_empty" },
                { label: "Vente", value: "vente" },
                { label: "Location", value: "location" },
              ]}
            />
          </FilterField>

          <FilterField label="Style architectural" name="style_architectural">
            <Select
              mode="multiple"
              allowClear
              style={{ width: "100%" }}
              options={[
                { label: "(vide)", value: "_empty" },
                { label: "Moderne", value: "moderne" },
                { label: "Classique", value: "classique" },
              ]}
            />
          </FilterField>


          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Form.Item label="Surface habitable - m2" name={["m2_habitable", 0]}>
              <InputNumber step={50} type="number" />
            </Form.Item>
            <Form.Item label=" " name={["m2_habitable", 1]}>
              <InputNumber step={50} type="number" />
            </Form.Item>
          </div>

           <div
             style={{
               display: "flex",
               justifyContent: "space-between",
               width: "100%",
             }}
           >
             <Form.Item label="Terrain - m2" name={["m2_terrain", 0]}>
               <InputNumber step={50} type="number" />
             </Form.Item>
             <Form.Item label=" " name={["m2_terrain", 1]}>
               <InputNumber step={50} type="number" />
             </Form.Item>
           </div>

           <div
             style={{
               display: "flex",
               justifyContent: "space-between",
               width: "100%",
             }}
           >
             <Form.Item label="Loyer par m2 - CHF" name={["Loyer_par_m2", 0]}>
               <InputNumber step={10} type="number" />
             </Form.Item>
             <Form.Item label=" " name={["Loyer_par_m2", 1]}>
               <InputNumber step={10} type="number" />
             </Form.Item>
           </div>

           <Form.Item name="showStatistics" valuePropName="checked">
             <Checkbox>Afficher les statistiques</Checkbox>
           </Form.Item>
        </>
      )}
    </Form>
  );
};

export default BiensOffMarketFilters;

const FilterField: FC<{
  label: string;
  name: string;
  children: ReactNode;
  className?: string;
}> = ({ label, name, children, className = "" }) => {
  return (
    <Form.Item label={label} name={name} className={className}>
      {children}
    </Form.Item>
  );
};
